import { useRequest } from 'ahooks';
import { Carousel, Col, Form, Input, Progress, Radio, Row, Select, Spin, Typography } from 'antd';
import { AxiosError } from 'axios';
import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { OnboardingSurveyV2, PatientFile } from 'services/api/models/SymptomTrackingDTO';
import { routes } from 'services/RouteService';
import { selectCurrentUser } from 'store/users/selectors';
import { Button } from 'components/ui';
import { colors, palette, screenHeight } from 'design';

const { Title } = Typography;

const useStyles = createUseStyles({
  carouselContainer: {
    // This looks stupid, but if you find something better, please let me know!
    height: screenHeight,
    '& .ant-carousel': {
      height: '100%',
      '& .slick-slider': {
        height: '100%',
        '& .slick-list': {
          height: '100%',
          '& .slick-track': {
            height: '100%',
            '& .slick-slide': {
              height: '100%',
            },
            '& .slick-slide > div': {
              height: '100%',
            },
          },
        },
      },
    },
  },
  carousel: {
    backgroundColor: colors.brand.accent,
  },
  carouselPage: {
    height: '100%',
    overflow: 'scroll',
    paddingBottom: 64,
  },
  carouselPageSection: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: 10,

    '& h3,p': { color: 'white' },
  },
  carouselPageSectionFullPageNoGrow: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: '10px 10px 64px 10px',
    flex: 0,

    '& .ant-row': {
      width: '100%',
      padding: '10px 16px',
    },
  },
  titleEmphasis: {
    color: colors.brand.accentSubtle,
  },
  dots: {
    bottom: '20px',

    '& li > button': {
      backgroundColor: palette.derived.tealMid,
    },
    '& li.slick-active > button': {
      backgroundColor: colors.brand.accent,
    },
  },
  answersContainer: {
    display: 'flex',
    maxWidth: '80%',
    margin: 'auto',
    flexDirection: 'column',
    '& h5': {
      color: 'white',
      marginTop: 16,
    },
    '& button': {
      height: 50,
      marginBottom: 20,
    },
    '& .anticon': {
      fontSize: 24,
    },

    '& .ant-btn-dashed': {
      backgroundColor: palette.mint[600],
      color: 'white',
    },
    '& .ant-btn-text': {
      color: 'white',
      height: 30,
    },
    '& .ant-btn-primary': {
      backgroundColor: 'white',
      color: colors.text.primary,
    },
    '& p': {
      color: 'white',
    },
  },
  fileContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',

    '& p': {
      color: 'white',
      marginBottom: 4,
    },

    '& .ant-form': {
      width: '100%',
    },

    '& .ant-radio-group': {
      width: '100%',
      '& .ant-radio-button-wrapper:not(:first-child)::before': {
        backgroundColor: colors.brand.accent,
        width: 2,
      },
    },
    '& .ant-radio-button-wrapper': {
      border: 'none',
      backgroundColor: palette.mint[600],
      color: 'white',
      width: '50%',
      borderInlineStart: 'none',
    },
    '& .ant-radio-button-wrapper-checked': {
      backgroundColor: 'white',
      color: colors.text.primary,
    },
  },
  postFileDescription: {
    fontSize: 17,
    margin: 0,
  },
  callToActionHelper: {
    fontSize: 12,
    marginBottom: 4,
  },
  yearSelect: {
    width: '100%',
    height: 40,
  },
  verticalSpace: {
    height: 16,
    width: '100%',
  },
  answerButtonWithIconText: {
    flex: 1,
  },
  infoText: {
    color: 'white',
    '& button': {
      marginTop: 15,
    },
  },
  symptomsAnswersContainer: {
    display: 'flex',
    maxWidth: '80%',
    margin: 'auto',
    justifyContent: 'space-around',

    '& button': {
      height: 50,
      marginBottom: 20,
      lineHeight: 1.3,
    },
    '& .ant-btn-dashed': {
      backgroundColor: palette.mint[600],
      color: 'white',
    },
    '& .ant-btn-primary': {
      backgroundColor: 'white',
      color: colors.brand.accentStrong,
    },
  },
  freeTextFieldForm: {
    width: '100%',
    '& textarea': {
      backgroundColor: palette.mint[200],
      height: 150,
    },
  },
  spinnerContainer: {
    '& .ant-spin-dot': {
      color: 'white',
    },
  },
});

const useMultiSelect = () => {
  const [values, setValues] = useState(new Set<string>());

  function isValueSelected(value: string) {
    return values.has(value);
  }

  function toggleValue(symptom: string) {
    if (isValueSelected(symptom)) setValues(oldValues => new Set([...oldValues].filter(item => item !== symptom)));
    else setValues(oldValues => new Set([...oldValues, symptom]));
  }

  function renderButton(valueKey: string, label: string, styleOverride?: React.CSSProperties) {
    return (
      <Button
        key={valueKey}
        style={{ width: '40%', textWrap: 'wrap', ...styleOverride }}
        variant={isValueSelected(valueKey) ? 'primary' : 'secondary'}
        onClick={() => toggleValue(valueKey)}
      >
        {label}
      </Button>
    );
  }

  return { values, renderButton, setValues };
};

const SymptomTrackingSurvey: React.FC = () => {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();
  const currentUser = useSelector(selectCurrentUser);

  const carousel = useRef(null);

  const isEditingOnboarding = Boolean((location.state as { editOnboarding?: boolean } | null)?.editOnboarding);

  const pathologiesOptions = [
    'ENDOMETRIOSIS',
    'SOPK',
    'FIBROME',
    'ADENOMYOMATOSIS',
    'BREAST_CANCER',
    'UTERUS_CANCER',
    'PMS',
  ];

  const {
    values: pathologiesValues,
    renderButton: renderPathologyButton,
    setValues: setPathologiesValues,
  } = useMultiSelect();

  const [showPathologiesField, setShowPathologiesField] = useState(false);
  const [pathologiesForm] = Form.useForm();
  const extraPathologiesValue = Form.useWatch('pathologies', pathologiesForm);

  const [patientFileForm] = Form.useForm<PatientFile>();

  // Question 2: Chronic symptoms
  const [chronicSymptomDuration, setChronicSymptomDuration] = useState<
    'NO' | 'LESS_THAN_ONE_YEAR' | 'ONE_TO_THREE_YEARS' | 'MORE_THAN_THREE_YEARS' | null
  >(null);
  const [symptomImpact, setSymptomImpact] = useState<
    'NONE' | 'LOW' | 'MODERATE' | 'IMPORTANT' | 'VERY_IMPORTANT' | null
  >(null);
  const [consultationCount, setConsultationCount] = useState<'NO' | 'ONE_TO_THREE' | 'MORE_THAN_THREE' | null>(null);

  const { run: loadPatientFile, loading: loadingPatientFile } = useRequest(
    () => ApiSymptomTrackingManager.getPatientFileMe(),
    {
      manual: true,
      onSuccess: file => {
        // Convert birth_year from number to string for the Select component
        patientFileForm.setFieldsValue({
          ...file,
          birth_year: file.birth_year ? String(file.birth_year) : undefined,
        } as any);
      },
      onError: error => {
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          // ignore 404 errors as not all users have filled out the patient file
          // for other errors, log for debugging
          console.error(error);
        }
      },
    },
  );

  const { run: loadOnboardingSurveyV2, loading: loadingOnboardingSurveyV2 } = useRequest(
    () => ApiSymptomTrackingManager.getOnboardingSurveyV2Me(),
    {
      manual: true,
      onSuccess: survey => {
        setPathologiesValues(new Set(survey.pathologies));
        if (survey.extra_pathologies) {
          setShowPathologiesField(true);
          pathologiesForm.setFieldsValue({ pathologies: survey.extra_pathologies });
        }
        setChronicSymptomDuration(survey.chronic_symptom_duration ?? null);
        setSymptomImpact(survey.symptom_impact ?? null);
        setConsultationCount(survey.consultation_count ?? null);
      },
      onError: error => {
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          // ignore 404 errors as not all users have filled out the onboarding survey v2
          // for other errors, log for debugging
          console.error(error);
        }
      },
    },
  );

  useEffect(() => {
    loadPatientFile();
    loadOnboardingSurveyV2();
  }, [loadOnboardingSurveyV2, loadPatientFile]);

  const nextPage = () => {
    (carousel?.current as any)?.next();
  };

  const { loading: postingOnboardingSurvey, run: postOnboardingSurvey } = useRequest(
    () => {
      const params: OnboardingSurveyV2 = {
        pathologies: Array.from(pathologiesValues),
        extra_pathologies: extraPathologiesValue || null,
        chronic_symptom_duration: chronicSymptomDuration,
        symptom_impact: chronicSymptomDuration && chronicSymptomDuration !== 'NO' ? symptomImpact : null,
        consultation_count: chronicSymptomDuration && chronicSymptomDuration !== 'NO' ? consultationCount : null,
      };
      return ApiSymptomTrackingManager.postOnboardingSurveyV2(params);
    },
    {
      manual: true,
      loadingDelay: 300,
      onSuccess: () => {
        if (isEditingOnboarding) {
          navigate(routes.settings.route);
        }
      },
      onError: e => {
        // TODO error handling
        console.error(e);
      },
    },
  );

  const { loading: postingPatientFile, run: postPatientFile } = useRequest(
    (formValues: PatientFile) => {
      return ApiSymptomTrackingManager.postPatientFile(formValues);
    },
    {
      manual: true,
      loadingDelay: 300,
    },
  );

  const renderProgress = (step: number, total: number) => {
    return (
      <Progress strokeColor="white" railColor={palette.mint[600]} percent={100 * (step / total)} showInfo={false} />
    );
  };

  const carouselPages = [
    {
      content: (
        <>
          <Row className={styles.carouselPageSection}>
            <Col>
              <Title level={3}>
                {t('onboarding.questionnaire.greeting')}{' '}
                <span className={styles.titleEmphasis}>{currentUser?.first_name}</span>, <br />
                <br />
              </Title>

              <p>{t('onboarding.questionnaire.description')}</p>
            </Col>
          </Row>
          <Row className={styles.answersContainer}>
            <Button onClick={() => nextPage()}>{t('onboarding.questionnaire.start')}</Button>
          </Row>
        </>
      ),
    },

    {
      content: (
        <>
          <Row className={styles.carouselPageSection}>
            {renderProgress(1, 4)}
            <Title level={3}>{t('onboarding.questionnaire.file.title')}</Title>
          </Row>
          <Row className={styles.fileContainer}>
            <Form
              form={patientFileForm}
              onFinish={formValues => {
                // Convert birth_year from string (Select value) back to number for API
                // formValues.birth_year is guaranteed to be present due to form validation
                postPatientFile({
                  ...formValues,
                  birth_year: Number(formValues.birth_year),
                });
                posthog.capture('onboarding_questionnaire.patient_file_submitted');
                nextPage();
              }}
            >
              <p>{t('onboarding.questionnaire.file.birthYear')}</p>
              <Form.Item
                name="birth_year"
                rules={[{ required: true, message: t('onboarding.questionnaire.file.requiredField') }]}
              >
                <Select
                  className={styles.yearSelect}
                  showSearch
                  placeholder={t('onboarding.questionnaire.file.birthYearPlaceholder')}
                  optionFilterProp="label"
                  filterSort={(optionA: { label: string; value: string }, optionB: { label: string; value: string }) =>
                    (optionB?.label ?? '').toLowerCase().localeCompare((optionA?.label ?? '').toLowerCase())
                  }
                  options={Array.from({ length: new Date().getFullYear() - 1900 }, (_, index) => ({
                    label: `${1900 + index}`,
                    value: `${1900 + index}`,
                  }))}
                />
              </Form.Item>

              <p>{t('onboarding.questionnaire.file.doYouTakeContraception')}</p>
              <Form.Item
                name="takes_contraception"
                rules={[{ required: true, message: t('onboarding.questionnaire.file.requiredField') }]}
              >
                <Radio.Group
                  size="large"
                  options={[
                    { label: t('onboarding.questionnaire.yes'), value: true },
                    { label: t('onboarding.questionnaire.no'), value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>

              <p>{t('onboarding.questionnaire.file.doYouHaveChildren')}</p>
              <Form.Item
                name="has_children"
                rules={[{ required: true, message: t('onboarding.questionnaire.file.requiredField') }]}
              >
                <Radio.Group
                  size="large"
                  options={[
                    { label: t('onboarding.questionnaire.yes'), value: true },
                    { label: t('onboarding.questionnaire.no'), value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>

              <p>{t('onboarding.questionnaire.file.areYouPregnant')}</p>
              <Form.Item
                name="is_pregnant"
                rules={[{ required: true, message: t('onboarding.questionnaire.file.requiredField') }]}
              >
                <Radio.Group
                  size="large"
                  options={[
                    { label: t('onboarding.questionnaire.yes'), value: true },
                    { label: t('onboarding.questionnaire.no'), value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>

              <p>{t('onboarding.questionnaire.file.areYouTryingToConceive')}</p>
              <Form.Item
                name="is_trying_to_conceive"
                rules={[{ required: true, message: t('onboarding.questionnaire.file.requiredField') }]}
              >
                <Radio.Group
                  size="large"
                  options={[
                    { label: t('onboarding.questionnaire.yes'), value: true },
                    { label: t('onboarding.questionnaire.no'), value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>

              <p>{t('onboarding.questionnaire.file.areYouTakingAnyMedication')}</p>
              <Form.Item
                name="is_taking_treatment"
                rules={[{ required: true, message: t('onboarding.questionnaire.file.requiredField') }]}
              >
                <Radio.Group
                  size="large"
                  options={[
                    { label: t('onboarding.questionnaire.yes'), value: true },
                    { label: t('onboarding.questionnaire.no'), value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <div className={styles.verticalSpace} />
              <div className={styles.verticalSpace} />
              <Button size="lg" block htmlType="submit" loading={postingPatientFile}>
                {t('onboarding.questionnaire.continue')}
              </Button>
            </Form>
          </Row>
        </>
      ),
    },

    {
      content: (
        <>
          <Row className={styles.carouselPageSection}>
            <Col>
              {renderProgress(2, 4)}
              <Title level={3}>{t('onboarding.questionnaire.pathologies.question')}</Title>
            </Col>
          </Row>
          <Row className={styles.symptomsAnswersContainer}>
            {pathologiesOptions.map(pathology =>
              renderPathologyButton(pathology, t(`onboarding.questionnaire.pathologies.options.${pathology}`), {
                width: '100%',
              }),
            )}
            {showPathologiesField && (
              <Form form={pathologiesForm} className={styles.freeTextFieldForm}>
                <Form.Item name="pathologies">
                  <Input.TextArea
                    variant="borderless"
                    autoCorrect="on"
                    placeholder={t('onboarding.questionnaire.pathologies.otherPathologiesPlaceholder')}
                  />
                </Form.Item>
              </Form>
            )}
            {!showPathologiesField && (
              <Button onClick={() => setShowPathologiesField(true)}>
                {t('onboarding.questionnaire.pathologies.otherPathologies')}
              </Button>
            )}
          </Row>
          <Row className={styles.carouselPageSection}>
            <Button
              onClick={() => {
                nextPage();
              }}
            >
              {showPathologiesField || pathologiesValues.size > 0
                ? t('onboarding.questionnaire.pathologies.savePathologies')
                : t('onboarding.questionnaire.pathologies.noPathologies')}
            </Button>
          </Row>
        </>
      ),
    },

    {
      content: (
        <>
          <Row className={styles.carouselPageSection}>
            <Col>
              {renderProgress(3, 4)}
              <Title level={3}>{t('onboarding.questionnaire.chronicSymptoms.question')}</Title>
              <p>{t('onboarding.questionnaire.chronicSymptoms.description')}</p>
            </Col>
          </Row>
          <Row className={styles.answersContainer}>
            {(['NO', 'LESS_THAN_ONE_YEAR', 'ONE_TO_THREE_YEARS', 'MORE_THAN_THREE_YEARS'] as const).map(option => (
              <Button
                key={option}
                variant={chronicSymptomDuration === option ? 'primary' : 'secondary'}
                onClick={() => {
                  setChronicSymptomDuration(option);
                  if (option === 'NO') {
                    setSymptomImpact(null);
                    setConsultationCount(null);
                  }
                }}
              >
                {t(`onboarding.questionnaire.chronicSymptoms.options.${option}`)}
              </Button>
            ))}
          </Row>

          {chronicSymptomDuration && chronicSymptomDuration !== 'NO' && (
            <>
              <Row className={styles.carouselPageSection}>
                <Col>
                  <Title level={4} style={{ color: 'white', marginTop: 24 }}>
                    {t('onboarding.questionnaire.chronicSymptoms.impact.question')}
                  </Title>
                </Col>
              </Row>
              <Row className={styles.answersContainer}>
                {(['NONE', 'LOW', 'MODERATE', 'IMPORTANT', 'VERY_IMPORTANT'] as const).map(option => (
                  <Button
                    key={option}
                    variant={symptomImpact === option ? 'primary' : 'secondary'}
                    onClick={() => setSymptomImpact(option)}
                  >
                    {t(`onboarding.questionnaire.chronicSymptoms.impact.options.${option}`)}
                  </Button>
                ))}
              </Row>

              <Row className={styles.carouselPageSection}>
                <Col>
                  <Title level={4} style={{ color: 'white', marginTop: 24 }}>
                    {t('onboarding.questionnaire.chronicSymptoms.consultation.question')}
                  </Title>
                </Col>
              </Row>
              <Row className={styles.answersContainer}>
                {(['NO', 'ONE_TO_THREE', 'MORE_THAN_THREE'] as const).map(option => (
                  <Button
                    key={option}
                    variant={consultationCount === option ? 'primary' : 'secondary'}
                    onClick={() => setConsultationCount(option)}
                  >
                    {t(`onboarding.questionnaire.chronicSymptoms.consultation.options.${option}`)}
                  </Button>
                ))}
              </Row>
            </>
          )}

          <Row className={styles.carouselPageSection}>
            <Button
              loading={postingOnboardingSurvey}
              onClick={() => {
                // Require chronic_symptom_duration to be selected
                if (!chronicSymptomDuration) {
                  return;
                }
                // If user selected a "yes" option, require impact and consultation
                if (chronicSymptomDuration !== 'NO') {
                  if (!symptomImpact || !consultationCount) {
                    return;
                  }
                }
                postOnboardingSurvey();
                posthog.capture('onboarding_questionnaire.onboarding_survey_submitted');
                if (!isEditingOnboarding) {
                  nextPage();
                }
                // When editing, navigation to settings happens in onSuccess callback
              }}
            >
              {t('onboarding.questionnaire.chronicSymptoms.continue')}
            </Button>
          </Row>
        </>
      ),
    },

    {
      content: (
        <>
          <Row className={styles.carouselPageSection}>
            <Col>
              <div className={styles.verticalSpace} />
              <div className={styles.verticalSpace} />
              <Title level={3}>
                {t('onboarding.questionnaire.congratulations')},{' '}
                <span className={styles.titleEmphasis}>{currentUser?.first_name}</span> ! <br />
              </Title>
              <div className={styles.verticalSpace} />
              <div className={styles.verticalSpace} />

              <p className={styles.postFileDescription}>
                <Trans i18nKey="onboarding.questionnaire.postFile.description" components={{ bold: <strong /> }} />
              </p>
            </Col>
          </Row>
          <Row className={styles.answersContainer}>
            <div className={styles.verticalSpace} />
            <div className={styles.verticalSpace} />
            <Button
              onClick={() => {
                navigate(routes.home.route);
              }}
            >
              {t('onboarding.questionnaire.postFile.letsGo')}
            </Button>
          </Row>
        </>
      ),
    },
  ];

  const effectiveCarouselPages = isEditingOnboarding ? carouselPages.slice(1, -1) : carouselPages;

  const renderOnboardingCarouselPageTemplate = (
    index: number,
    children: { content?: React.ReactElement; scrollableContent?: React.ReactElement },
  ) => {
    return (
      <div key={index} className={styles.carouselPage}>
        {children.content && <Row className={styles.carouselPageSectionFullPageNoGrow}>{children.content}</Row>}
      </div>
    );
  };

  const isLoadingProfile = loadingPatientFile || loadingOnboardingSurveyV2;

  return (
    <div className={styles.carouselContainer}>
      {isLoadingProfile ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <Spin size="large" className={styles.spinnerContainer} />
          <p style={{ color: 'white' }}>{t('common.loading')}</p>
        </div>
      ) : (
        <Carousel ref={carousel} className={styles.carousel} infinite={false} dots={{ className: styles.dots }}>
          {effectiveCarouselPages.map((page, index) => renderOnboardingCarouselPageTemplate(index, page))}
        </Carousel>
      )}
    </div>
  );
};

export default SymptomTrackingSurvey;
